import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useCustomDialog } from '../context/DialogContext';
import '../styles/superadmin.css';

import useAutoUpdate from '../hooks/useAutoUpdate';
import useWindowFocus from '../hooks/useWindowFocus';

const BASE = 'https://menu.searchisend.in';

const SuperadminDashboard = () => {
    useAutoUpdate('superadmin');
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const token = localStorage.getItem('saas_superadmin_token');
    const userString = localStorage.getItem('saas_superadmin_user');
    
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);

    // Cloning state
    const [cloningCustomer, setCloningCustomer] = useState(null);
    const [cloneData, setCloneData] = useState({ new_username: '', new_email: '', new_slug: '' });
    const [cloneLoading, setCloneLoading] = useState(false);

    const fetchCustomers = async (searchOverride = search, statusOverride = status, silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_customers&search=${encodeURIComponent(searchOverride)}&status=${encodeURIComponent(statusOverride)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401) {
                navigate('/superadmin_v2_secure_login');
                return;
            }
            const data = await res.json();
            if (data.success) {
                setCustomers(data.customers);
            } else if (data.message && (data.message.includes('Token') || data.message.includes('Authorization'))) {
                localStorage.removeItem('saas_superadmin_token');
                localStorage.removeItem('saas_superadmin_user');
                navigate('/superadmin_v2_secure_login');
            }
        } catch (err) {
            console.error('Error fetching customers', err);
            // Handle Network Errors gracefully (e.g. Wi-Fi dropping/reconnecting)
            if (err.name === 'TypeError' && err.message.includes('fetch')) {
                console.warn('Network issue fetching customers. Retrying in 5 seconds...');
                setTimeout(() => fetchCustomers(searchOverride, statusOverride, true), 5000);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!token || !userString) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchCustomers(search, status, false);

        const pollInterval = setInterval(() => {
            fetchCustomers(search, status, true);
        }, 15000); // Polling reduced to 15s to save network load, relying on focus instead

        return () => clearInterval(pollInterval);
        // eslint-disable-next-line
    }, [search, status]);

    // Re-fetch instantly when user brings tab to focus
    useWindowFocus(() => {
        if (token && userString) {
            fetchCustomers(search, status, true);
        }
    });

    const handleCloneSubmit = async (e) => {
        e.preventDefault();
        if (!cloneData.new_username || !cloneData.new_email || !cloneData.new_slug) {
            await customAlert('Please fill out all fields to clone.', 'Cloning Error', 'warning');
            return;
        }
        setCloneLoading(true);
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=clone_customer`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source_id: cloningCustomer.id,
                    new_username: cloneData.new_username,
                    new_email: cloneData.new_email,
                    new_slug: cloneData.new_slug
                })
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Customer cloned successfully!', 'Success', 'success');
                setCloningCustomer(null);
                setCloneData({ new_username: '', new_email: '', new_slug: '' });
                fetchCustomers();
            } else {
                await customAlert(data.message || 'Error cloning customer', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error while cloning', 'Error', 'danger');
        } finally {
            setCloneLoading(false);
        }
    };

    const handleDeleteCustomer = async (id) => {
        if (!await customConfirm("ARE YOU SURE? This will delete the customer and all their data permanently.", "Delete Partner", "danger")) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_customer`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: id })
            });
            const data = await res.json();
            if (data.success) {
                await customAlert('Customer deleted successfully', 'Success', 'success');
                fetchCustomers();
            } else {
                await customAlert(data.message || 'Error deleting customer', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error while deleting', 'Error', 'danger');
        }
    };

    const handleImpersonate = async (customerId, path) => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=generate_customer_token`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customer_id: customerId })
            });
            const data = await res.json();
            if (data.success) {
                localStorage.setItem('saas_token', data.token);
                localStorage.setItem('saas_user', JSON.stringify(data.user));
                window.open(`/manage_v2_secure_auth${path}`, '_blank');
            } else {
                await customAlert(data.message || 'Error generating customer session', 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error while generating token', 'Error', 'danger');
        }
    };

    const handleExport = async (customerId, restaurantName) => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=export_menu_json&customer_id=${customerId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                const blob = new Blob([JSON.stringify(data.menu, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${data.restaurant_name}_menu.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                await customAlert(data.message || 'Error exporting menu', 'Export Error', 'danger');
            }
        } catch (err) {
            await customAlert('Network error during export', 'Error', 'danger');
        }
    };

    const getStatusClass = (st) => {
        if (st === 'Active') return 'status-active';
        if (st === 'Paused') return 'status-paused';
        return 'status-expired';
    };

    return (
        <SuperAdminLayout>
            <h2 className="sa-page-title">Partners Dashboard</h2>
            <p className="sa-page-subtitle">Monitor and manage all SaaS restaurant accounts across the network.</p>

            <section className="sa-stats-grid">
                <div className="sa-stat-card">
                    <div className="sa-stat-label">Total Partners</div>
                    <div className="sa-stat-value">{customers.length}</div>
                </div>
                <div className="sa-stat-card">
                    <div className="sa-stat-label">Active MRR</div>
                    <div className="sa-stat-value" style={{ color: 'var(--success)' }}>₹ {customers.filter(c => c.status === 'Active').length * 499}</div>
                </div>
                <div className="sa-stat-card">
                    <div className="sa-stat-label">Network Page Views</div>
                    <div className="sa-stat-value">
                        {customers.reduce((acc, c) => acc + (Number(c.total_views) || 0), 0).toLocaleString()}
                    </div>
                </div>
            </section>

            <div className="sa-action-bar">
                <div className="sa-filters">
                    <i className="fas fa-search" style={{ color: 'var(--text-secondary)' }}></i>
                    <input 
                        type="text" 
                        placeholder="Search partners, email, or username..." 
                        className="sa-search-input"
                        style={{ border: 'none', background: 'transparent', boxShadow: 'none', padding: '5px' }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onBlur={() => fetchCustomers()}
                    />
                    <div style={{ width: '1px', height: '24px', background: 'var(--border-color)', margin: '0 10px' }}></div>
                    <select 
                        className="sa-select"
                        style={{ border: 'none', background: 'transparent', boxShadow: 'none', padding: '5px', fontWeight: 500 }}
                        value={status} 
                        onChange={(e) => { setStatus(e.target.value); fetchCustomers(search, e.target.value); }}
                    >
                        <option value="">All Statuses</option>
                        <option value="Active">Active</option>
                        <option value="Paused">Paused</option>
                        <option value="Expired">Expired</option>
                        <option value="Pending_Verification">Pending</option>
                    </select>
                </div>
                <button onClick={() => fetchCustomers()} className="sa-primary-btn"><i className="fas fa-sync-alt"></i> Refresh Data</button>
            </div>

            <div className="sa-table-container">
                <table className="sa-table">
                    <thead>
                        <tr>
                            <th style={{ width: '50px', textAlign: 'center' }}>S.No</th>
                            <th>Partner Profile</th>
                            <th>Status</th>
                            <th>Renewal Date</th>
                            <th>Features</th>
                            <th style={{ textAlign: 'right' }}>Quick Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x"></i></td></tr>
                        ) : customers.length === 0 ? (
                            <tr><td colSpan="6" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No partners found matching your search.</td></tr>
                        ) : customers.map((c, index) => (
                            <tr key={c.id}>
                                <td style={{ textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600 }}>
                                    {index + 1}
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {c.logo_url ? (
                                            <img src={c.logo_url} alt="logo" style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border-color)' }} />
                                        ) : (
                                            <div style={{ width: '42px', height: '42px', borderRadius: '8px', background: 'var(--info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--info)', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                                {c.restaurant_name.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <div>
                                            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{c.restaurant_name}</div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>@{c.username} &bull; {c.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`status-badge ${getStatusClass(c.status)}`}>
                                        {c.status}
                                    </span>
                                </td>
                                <td>
                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{new Date(c.renewal_date).toLocaleDateString()}</div>
                                    {new Date(c.renewal_date) < new Date() && c.status !== 'Expired' && (
                                        <div style={{ fontSize: '0.7rem', color: 'var(--danger)', marginTop: '2px', fontWeight: 600 }}>Needs Renewal</div>
                                    )}
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <i className={`fas fa-bell ${Number(c.has_push_notification) === 1 ? '' : 'fa-bell-slash'}`} title="Push Notifications" style={{ color: Number(c.has_push_notification) === 1 ? 'var(--info)' : '#cbd5e1', opacity: Number(c.has_push_notification) === 1 ? 1 : 0.4 }}></i>
                                        <i className="fas fa-ticket-alt" title="Coupons" style={{ color: Number(c.has_coupon_system) === 1 ? 'var(--success)' : '#cbd5e1', opacity: Number(c.has_coupon_system) === 1 ? 1 : 0.4 }}></i>
                                        <i className={`${Number(c.has_offer_popup) === 1 ? 'fas' : 'far'} fa-star`} title="Offers" style={{ color: Number(c.has_offer_popup) === 1 ? 'var(--warning)' : '#cbd5e1', opacity: Number(c.has_offer_popup) === 1 ? 1 : 0.4 }}></i>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap', maxWidth: '350px', marginLeft: 'auto' }}>
                                        <button onClick={() => navigate('/superadmin_v2_secure/edit_customer/' + c.id)} className="sa-action-btn primary" title="Edit Partner">
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        <button onClick={() => handleImpersonate(c.id, '/')} className="sa-action-btn success" title="Login as Partner">
                                            <i className="fas fa-sign-in-alt"></i> Login
                                        </button>
                                        <button onClick={() => navigate('/superadmin_v2_secure/view_menu/' + c.id)} className="sa-action-btn" title="View/Edit Menu">
                                            <i className="fas fa-list"></i> Menu
                                        </button>
                                        <button onClick={() => handleImpersonate(c.id, '/manage-offers')} className="sa-action-btn" title="Manage Offers" style={{ color: 'var(--warning)' }}>
                                            <i className="fas fa-tags"></i>
                                        </button>
                                        <button onClick={() => handleExport(c.id, c.restaurant_name)} className="sa-action-btn" title="Export Menu JSON" style={{ color: 'var(--info)' }}>
                                            <i className="fas fa-file-export"></i>
                                        </button>
                                        <button onClick={() => setCloningCustomer(c)} className="sa-action-btn" title="Clone Restaurant">
                                            <i className="fas fa-copy"></i>
                                        </button>
                                        <button onClick={() => handleDeleteCustomer(c.id)} className="sa-action-btn danger" title="Delete Partner">
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* CLONE MODAL */}
            {cloningCustomer && (
                <div className="sa-modal-overlay">
                    <div className="sa-modal">
                        <div className="sa-modal-header">
                            <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--text-primary)' }}>Clone Partner Profile</h2>
                            <button onClick={() => setCloningCustomer(null)} className="sa-modal-close"><i className="fas fa-times"></i></button>
                        </div>
                        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                            You are duplicating <strong>{cloningCustomer.restaurant_name}</strong>. This copies the profile details, configuration settings, categories, and all menu items to a new account.
                        </p>
                        <form onSubmit={handleCloneSubmit}>
                            <div className="sa-form-group">
                                <label className="sa-form-label">New Username</label>
                                <input 
                                    type="text" required
                                    className="sa-input" style={{ width: '100%' }}
                                    value={cloneData.new_username}
                                    placeholder="e.g. rohan_cafe"
                                    onChange={(e) => setCloneData({...cloneData, new_username: e.target.value.toLowerCase().trim()})}
                                />
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">New Email</label>
                                <input 
                                    type="email" required
                                    className="sa-input" style={{ width: '100%' }}
                                    value={cloneData.new_email}
                                    onChange={(e) => setCloneData({...cloneData, new_email: e.target.value})}
                                />
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">New Menu URL Slug</label>
                                <input 
                                    type="text" required
                                    className="sa-input" style={{ width: '100%' }}
                                    value={cloneData.new_slug}
                                    placeholder="e.g. rohan-cafe"
                                    onChange={(e) => setCloneData({...cloneData, new_slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                                />
                            </div>
                            <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem' }}>
                                <button type="submit" disabled={cloneLoading} className="sa-primary-btn" style={{ flex: 1, padding: '12px' }}>
                                    {cloneLoading ? <><i className="fas fa-circle-notch fa-spin"></i> Cloning...</> : <><i className="fas fa-copy"></i> Start Clone</>}
                                </button>
                                <button type="button" onClick={() => setCloningCustomer(null)} className="sa-secondary-btn" style={{ flex: 1, padding: '12px' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </SuperAdminLayout>
    );
};

export default SuperadminDashboard;
